import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

const TemplateLibrary = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [packageType, setPackageType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchAvailableTemplates();
  }, [isAuthenticated, navigate]);

  const fetchAvailableTemplates = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        console.error('No access token found');
        navigate('/login');
        return;
      }

      const response = await fetch('/api/documents/templates', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const CATEGORY_LABELS = { employment: 'Contract', client_relations: 'Policy' };
        setTemplates((data.templates || data).map(t => ({
          ...t,
          category: CATEGORY_LABELS[String(t.category || '').toLowerCase()] || t.category
        })));
        setPackageType(data.package_type || 'standard');
      } else {
        console.error('Failed to fetch templates');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories from templates
  const categories = useMemo(() => {
    const cats = [...new Set(templates.map(t => t.category))].sort();
    return ['All', ...cats];
  }, [templates]);

  // Filter templates by category and search
  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      const matchesCategory = selectedCategory === 'All' || template.category === selectedCategory;
      const matchesSearch = !searchQuery || 
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [templates, selectedCategory, searchQuery]);

  // Get count per category
  const categoryCounts = useMemo(() => {
    const counts = { All: templates.length };
    templates.forEach(t => {
      counts[t.category] = (counts[t.category] || 0) + 1;
    });
    return counts;
  }, [templates]);

  const handleTemplateSelect = (template) => {
    navigate(`/documents/customize/${template.id}`);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Form': '📝',
      'Policy': '📋',
      'Letter': '✉️',
      'Contract': '📄',
      'Guide': '📚',
      'Training': '🎓',
      'Risk Assessment': '⚠️',
      'Rental': '🏠',
      'Procedure': '📑'
    };
    return icons[category] || '📄';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Form': { bg: '#dbeafe', text: '#1e40af' },
      'Policy': { bg: '#dcfce7', text: '#166534' },
      'Letter': { bg: '#fef3c7', text: '#92400e' },
      'Contract': { bg: '#f3e8ff', text: '#7c3aed' },
      'Guide': { bg: '#ffedd5', text: '#c2410c' },
      'Training': { bg: '#cffafe', text: '#0e7490' },
      'Risk Assessment': { bg: '#fee2e2', text: '#dc2626' },
      'Rental': { bg: '#e0e7ff', text: '#4338ca' },
      'Procedure': { bg: '#f5f5f4', text: '#57534e' }
    };
    return colors[category] || { bg: '#f3f4f6', text: '#374151' };
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '2px solid #e5e7eb',
            borderTop: '2px solid #2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280', margin: 0 }}>Loading templates...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f9fafb',
      padding: '32px 0',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        maxWidth: '1152px',
        margin: '0 auto',
        padding: '0 16px'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={() => navigate('/documents')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              background: 'white',
              cursor: 'pointer',
              marginBottom: '16px'
            }}
          >
            ← Back to Dashboard
          </button>
          <h1 style={{
            fontSize: '30px',
            fontWeight: 'bold',
            color: '#111827',
            margin: '0 0 8px 0'
          }}>Template Library</h1>
          <p style={{ color: '#6b7280', margin: '0 0 16px 0' }}>
            Welcome, {user?.name || 'User'}. Choose from {templates.length} available templates
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div style={{
          background: 'white',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'center'
          }}>
            {/* Search Input */}
            <div style={{ flex: '1', minWidth: '200px' }}>
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Category Filter */}
            <div style={{ minWidth: '180px' }}>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat} ({categoryCounts[cat] || 0})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Category Pills */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            marginTop: '16px'
          }}>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '9999px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  border: 'none',
                  transition: 'all 0.2s',
                  background: selectedCategory === cat ? '#2563eb' : '#f3f4f6',
                  color: selectedCategory === cat ? 'white' : '#4b5563'
                }}
              >
                {cat !== 'All' && getCategoryIcon(cat)} {cat} ({categoryCounts[cat] || 0})
              </button>
            ))}
          </div>
        </div>

        {/* Results count */}
        <p style={{
          color: '#6b7280',
          fontSize: '14px',
          marginBottom: '16px'
        }}>
          Showing {filteredTemplates.length} of {templates.length} templates
          {selectedCategory !== 'All' && ` in ${selectedCategory}`}
          {searchQuery && ` matching "${searchQuery}"`}
        </p>

        {/* Template Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '24px'
        }}>
          {filteredTemplates.map((template) => {
            const catColor = getCategoryColor(template.category);
            return (
              <div
                key={template.id}
                style={{
                  background: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s, transform 0.2s'
                }}
                onClick={() => handleTemplateSelect(template)}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ padding: '24px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: catColor.bg,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px'
                    }}>
                      {getCategoryIcon(template.category)}
                    </div>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: catColor.text,
                      background: catColor.bg,
                      padding: '4px 10px',
                      borderRadius: '9999px'
                    }}>
                      {template.category}
                    </span>
                  </div>
                  
                  {template.template_key && (
                    <div style={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#6b7280',
                      letterSpacing: '0.04em',
                      margin: '0 0 6px 0'
                    }}>
                      {template.template_key}
                    </div>
                  )}

                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: '0 0 8px 0'
                  }}>
                    {template.name}
                  </h3>
                  
                  <p style={{
                    color: '#6b7280',
                    fontSize: '14px',
                    margin: '0 0 16px 0',
                    lineHeight: '1.4',
                    minHeight: '40px'
                  }}>
                    {template.description}
                  </p>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      color: '#059669',
                      fontWeight: '500'
                    }}>
                      ✓ Included
                    </span>
                    <button
                      style={{
                        color: '#2563eb',
                        fontWeight: '500',
                        fontSize: '14px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      Customize →
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredTemplates.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '48px 0'
          }}>
            <div style={{
              width: '96px',
              height: '96px',
              background: '#f3f4f6',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: '48px'
            }}>
              🔍
            </div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '500',
              color: '#111827',
              margin: '0 0 8px 0'
            }}>No templates found</h3>
            <p style={{ color: '#6b7280', margin: 0 }}>
              Try adjusting your search or filter criteria
            </p>
            <button
              onClick={() => { setSelectedCategory('All'); setSearchQuery(''); }}
              style={{
                marginTop: '16px',
                padding: '8px 16px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateLibrary;